import { useEffect, useState } from 'react';
import {
  createShopCheckout,
  getShopCollection,
  getShopCollections,
  getShopProduct,
  getShopProducts,
  isShopifyConfigured,
  type ShopCartLineInput,
  type ShopCollection,
  type ShopCheckoutSession,
  type ShopProduct,
} from '../lib/shopify';

export function useShopCollections() {
  const [collections, setCollections] = useState<ShopCollection[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!isShopifyConfigured) {
        setCollections([]);
        setError(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const next = await getShopCollections();
        if (!cancelled) {
          setCollections(next);
          setError(null);
        }
      } catch (err: any) {
        if (!cancelled) {
          setCollections([]);
          setError(err);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  return { collections, error, isLoading, isConfigured: isShopifyConfigured };
}

export function useShopProducts() {
  const [products, setProducts] = useState<ShopProduct[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!isShopifyConfigured) {
        setProducts([]);
        setError(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const next = await getShopProducts();
        if (!cancelled) {
          setProducts(next);
          setError(null);
        }
      } catch (err: any) {
        if (!cancelled) {
          setProducts([]);
          setError(err);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  return { products, error, isLoading, isConfigured: isShopifyConfigured };
}

export function useShopProduct(handle: string | null) {
  const [product, setProduct] = useState<ShopProduct | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!isShopifyConfigured || !handle) {
        setProduct(null);
        setError(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const next = await getShopProduct(handle);
        if (!cancelled) {
          setProduct(next);
          setError(null);
        }
      } catch (err: any) {
        if (!cancelled) {
          setProduct(null);
          setError(err);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [handle]);

  return { product, error, isLoading, isConfigured: isShopifyConfigured };
}

export function useShopCollection(handle: string | null) {
  const [collection, setCollection] = useState<ShopCollection | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!isShopifyConfigured || !handle) {
        setCollection(null);
        setError(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const next = await getShopCollection(handle);
        if (!cancelled) {
          setCollection(next);
          setError(null);
        }
      } catch (err: any) {
        if (!cancelled) {
          setCollection(null);
          setError(err);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [handle]);

  // Append the next page of products to the existing collection. No-op
  // when there's nothing to load or a fetch is already in flight.
  // Doesn't take a cancellation token because the next render after
  // success uses the same products[] anyway — bail-on-handle-change
  // is enforced by the page itself which discards the hook on
  // navigation.
  const loadMore = async () => {
    if (!handle || !collection || isLoadingMore) return;
    if (!collection.productsPageInfo.hasNextPage) return;
    setIsLoadingMore(true);
    try {
      const nextPage = await getShopCollection(handle, {
        after: collection.productsPageInfo.endCursor,
      });
      if (!nextPage) return;
      setCollection({
        ...collection,
        products: [...collection.products, ...nextPage.products],
        productsPageInfo: nextPage.productsPageInfo,
      });
    } catch (err: any) {
      setError(err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  return {
    collection,
    error,
    isLoading,
    isLoadingMore,
    hasNextPage: collection?.productsPageInfo.hasNextPage ?? false,
    loadMore,
    isConfigured: isShopifyConfigured,
  };
}

export function useShopCheckout() {
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const startCheckout = async (lines: ShopCartLineInput[], email?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await createShopCheckout(lines, email);
      setIsLoading(false);
      return { data, error: null as Error | null };
    } catch (err: any) {
      setError(err);
      setIsLoading(false);
      return { data: null as ShopCheckoutSession | null, error: err as Error };
    }
  };

  return { startCheckout, error, isLoading, isConfigured: isShopifyConfigured };
}